import { useEffect, useState } from 'react'

import { useCreateFacility } from '../hooks/useCreateFacility'
import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useToast } from '@chakra-ui/react'

import { Box, Modal, Tabs } from '@hivewatch/dark'

import { CreateFacilityInput, FacilityType } from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

import { FormInputsIProps } from '../types/types'
import { CSVParseResultI, CSV_UploadStatus } from '../utils/csvParser'
import {
  getErrorMessage,
  getFacilityTimezone,
  isBulkUploadButtonDisabled,
} from '../utils/utils'
import { BulkUploadFacilitiesForm } from './BulkUploadFacilitiesForm'
import { DarkCreateFacilityForm } from './DarkCreateFacilityForm'

interface DarkCreateFacilityModalIProps {
  isOpen: boolean
  onClose: () => void
}

const INDIVIDUAL_TAB = 'individual'
const BULK_UPLOAD_TAB = 'bulk'

/**
 * Rebrand variant of {@link CreateFacilityModal} built on `@hivewatch/dark`.
 * Both tabs, the per-row bulk upload status tracking and every toast fire
 * exactly as in the legacy modal. The bulk tab keeps the shared CSV table,
 * which has no dark equivalent yet, and is keyed to the theme through the
 * form's `colorScheme` prop.
 */
export const DarkCreateFacilityModal = ({
  isOpen,
  onClose,
}: DarkCreateFacilityModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const form = useForm<FormInputsIProps>({
    mode: 'onChange',
  })
  const { handleSubmit, formState, watch } = form
  const { isValid, isSubmitting } = formState
  const type = watch('type')?.value
  const { showSuccess, showError } = useToasts()
  const { create, facilityTypes } = useCreateFacility()
  const [parseResult, setParseResult] = useState<CSVParseResultI | null>(null)
  const [activeTab, setActiveTab] = useState(INDIVIDUAL_TAB)
  const [isCreating, setIsCreating] = useState(false)
  const [isBulkUploadComplete, setBulkUploadIsComplete] = useState(false)

  const isBulkUpload = activeTab === BULK_UPLOAD_TAB

  const toast = useToast()

  /**
   * Handles bulk facility creation from parsed CSV data
   */
  const handleBulkCreate = async () => {
    if (!parseResult || parseResult.data.length === 0) {
      showError(t('facilities:crud_no_facilities_to_create'))
      return
    }

    setIsCreating(true)
    const results = {
      successful: [] as string[],
      failed: [] as { name: string; error: string }[],
    }

    // Initialize all facilities with pending status
    const facilitiesWithStatus = parseResult.data.map((facility) => ({
      ...facility,
      uploadStatus: CSV_UploadStatus.Pending,
    }))
    setParseResult({ ...parseResult, data: facilitiesWithStatus })

    for (let i = 0; i < facilitiesWithStatus.length; i++) {
      const facility = facilitiesWithStatus[i]
      const input: CreateFacilityInput = {
        name: facility.name,
        shortName: facility.shortName.toLocaleUpperCase(),
        type: FacilityType.Normal,
        address: facility.address ? [facility.address] : null,
        latitude: parseFloat(facility.lat),
        longitude: parseFloat(facility.lon),
        timeZone: facility.timezone,
      }

      try {
        await create(input)
        results.successful.push(facility.name)

        // Update status to success
        setParseResult((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            data: prev.data.map((f, index) =>
              index === i ? { ...f, uploadStatus: CSV_UploadStatus.Success } : f
            ),
          }
        })
      } catch (e) {
        results.failed.push({
          name: facility.name,
          error: e.message,
        })

        // Update status to failed with error
        setParseResult((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            data: prev.data.map((f, index) =>
              index === i
                ? {
                    ...f,
                    uploadStatus: CSV_UploadStatus.Failed,
                    uploadError: e.message,
                  }
                : f
            ),
          }
        })
      }
    }

    setIsCreating(false)
    setBulkUploadIsComplete(true)

    // Show results
    if (results.successful.length > 0) {
      const numberCreated = results.successful.length

      toast({
        title: t('facilities:crud_bulk_success_title'),
        description: tVar(t, 'facilities:crud_bulk_success_description', {
          count: numberCreated,
        }),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      })
    }

    if (results.failed.length > 0) {
      const numberFailed = results.failed.length

      toast({
        title: t('common:error_label'),
        description: tVar(t, 'facilities:crud_bulk_error_description', {
          count: numberFailed,
        }),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      })
    }
  }

  /**
   * Handles individual facility creation
   */
  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    const input: CreateFacilityInput = {
      name: values.name,
      shortName: values.shortName.toLocaleUpperCase(),
      type: values.type?.value,
      address: values?.address ? [values.address] : null,
      latitude: parseFloat(values.latitude),
      longitude: parseFloat(values.longitude),
      ...(type !== FacilityType.Market && {
        timeZone: getFacilityTimezone(values.latitude, values.longitude),
      }),
    }
    try {
      await create(input)
      const successMsg = tVar(t, 'facilities:crud_success_created_facility', {
        name: values.name,
      })
      showSuccess(successMsg)
      onClose()
    } catch (e) {
      showError(getErrorMessage(e, values.name, 'creating'))
    }
  }

  /**
   * Handles form submission based on active tab
   */
  const handleFormSubmit = async () => {
    if (isBulkUpload) {
      // When bulk submit is completed the Submit turns into a Done button
      if (isBulkUploadComplete) {
        onClose()
      } else {
        await handleBulkCreate()
      }
    } else {
      await handleSubmit(onSubmit)()
    }
  }

  const handleClose = () => {
    if (!isSubmitting && !isCreating) {
      onClose()
    }
  }

  useEffect(() => {
    if (!parseResult) {
      setBulkUploadIsComplete(false)
    }
  }, [parseResult])

  // Widen the modal while the parsed-facility table is on screen.
  const maxW = isBulkUpload && parseResult?.data?.length ? '90vw' : '700px'

  const getCreateFacilityButtonTitle = () => {
    if (isBulkUploadComplete) {
      return t('common:done')
    }
    return isBulkUpload
      ? t('facilities:crud_create_facilities_button')
      : t('facilities:crud_create_facility_button')
  }

  const tabItems = [
    {
      id: INDIVIDUAL_TAB,
      label: t('facilities:crud_tab_create_individual'),
    },
    { id: BULK_UPLOAD_TAB, label: t('facilities:crud_tab_bulk_upload') },
  ]

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={
        isBulkUpload ? isBulkUploadButtonDisabled(parseResult) : !isValid
      }
      isSaveLoading={isSubmitting || isCreating}
      maxW={maxW}
      onClose={handleClose}
      onSave={handleFormSubmit}
      saveButtonLabel={getCreateFacilityButtonTitle()}
      showCancelButton={!isBulkUploadComplete}
      title={t('facilities:crud_create_title')}
    >
      <Tabs
        activeId={activeTab}
        colorScheme={colorScheme}
        items={tabItems}
        onChange={setActiveTab}
      />
      <Box mt='16px'>
        {isBulkUpload ? (
          <BulkUploadFacilitiesForm
            colorScheme={colorScheme}
            form={form}
            onParseResultChange={setParseResult}
            parseResult={parseResult}
          />
        ) : (
          <DarkCreateFacilityForm
            colorScheme={colorScheme}
            facilityTypes={facilityTypes}
            form={form}
            isOpen={isOpen}
          />
        )}
      </Box>
    </Modal>
  )
}
